import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import portfolioData from '../../data/portfolioData';
import Reveal from '../Reveal/Reveal';
import './Contact.css';

const Contact = () => {
  const formRef = useRef(null);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const sendEmail = async (event) => {
    event.preventDefault();
    const form = formRef.current;
    if (!form) return;

    const data = new FormData(form);
    const name = String(data.get('user_name') || '').trim();
    const email = String(data.get('user_email') || '').trim();
    const message = String(data.get('message') || '').trim();

    if (!name || !email || !message) {
      setStatus({ type: 'error', message: 'Please fill in name, email, and message.' });
      return;
    }

    if (!isValidEmail(email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await emailjs.sendForm('service_u27l74h', 'template_q4pzu6g', form, {
        publicKey: 'M-MqEg9g_y4iyrQ_v',
      });
              setStatus({ type: 'success', message: "Message sent. I'll get back to you soon." });
      form.reset();
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please email me directly or try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section contact" id="contact">
      <div className="container contact__grid">
        <Reveal>
          <div className="section-head">
            <p className="section-eyebrow">Contact</p>
            <h2 className="section-title">Let's build something</h2>
            <p className="section-lead">
              Open to full-time roles, contract work, and collaborations on mobile, full-stack, and AI products.
            </p>
          </div>

          <ul className="contact__info">
            <li>
              <MailOutlineIcon />
              <div>
                <span>Email</span>
                <a href={`mailto:${portfolioData.email}`}>{portfolioData.email}</a>
              </div>
            </li>
            <li>
              <PhoneIphoneIcon />
              <div>
                <span>Phone</span>
                <a href={`tel:${portfolioData.phoneRaw}`}>{portfolioData.phone}</a>
              </div>
            </li>
            <li>
              <PlaceOutlinedIcon />
              <div>
                <span>Location</span>
                <p>{portfolioData.location}</p>
              </div>
            </li>
          </ul>

          <div className="contact__socials">
            {portfolioData.socials.map((social) => {
              const Icon = social.icon;
              return (
                <a key={social.id} href={social.href} target="_blank" rel="noreferrer" aria-label={social.label}>
                  <Icon />
                </a>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={1}>
          <form className="contact__form" ref={formRef} onSubmit={sendEmail} noValidate>
            <div className="contact__row">
              <label>
                Name
                <input name="user_name" type="text" autoComplete="name" required />
              </label>
              <label>
                Phone
                <input name="user_number" type="tel" autoComplete="tel" placeholder="Optional" />
              </label>
            </div>
            <label>
              Email
              <input name="user_email" type="email" autoComplete="email" required />
            </label>
            <label>
              Message
              <textarea name="message" rows={5} required />
            </label>

            {status.message && (
              <p className={`contact__status contact__status--${status.type}`} role="status">
                {status.message}
              </p>
            )}

            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send message'}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
